// Analytics/services/endpoints/radiology.js
import axios from 'axios';
import { datalogger } from '../../../../../utils/index.jsx';
import { shouldServeMock } from '../demoMock';

const ANALYTICS_BASE_URL = import.meta.env.VITE_APP_API_URL + "/analytics";

// ============================================
// MOCK CONFIGURATION
// ============================================
const MOCK_CONFIG = {
    enabled: import.meta.env.VITE_MOCK_RADIOLOGY === 'true',
    endpoints: {
        'total-conducted': true,
        'turnaround-time': true,
        'patient-count': true,
        'monthly-trends': true,
        'location': true,
    },
    delay: 300
};

// ============================================
// MOCK DATA GENERATORS
// ============================================

const getMockTotalScansResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Scans", data: [28, 32, 30, 35, 33, 38, 40] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 236
    }
});

const getMockTurnaroundTimeResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Turnaround Time (Hours)", data: [4.5, 5.0, 4.8, 5.2, 4.9, 5.1, 5.0] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 4.9
    }
});

const getMockPatientCountResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Patients", data: [20, 25, 22, 28, 26, 30, 32] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 183
    }
});

const getMockMonthlyTrendsResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "CT Scans", data: [15, 18, 16, 20, 19, 22, 24] },
            { name: "MRI", data: [8, 10, 9, 12, 11, 13, 14] },
            { name: "X-Ray", data: [5, 4, 5, 3, 3, 3, 2] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 236
    }
});

const getMockLocationResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Scans", data: [120, 80, 36] }
        ],
        categories: ["Main Radiology", "Emergency Radiology", "Outpatient Radiology"],
        count: 236
    }
});

// Mock registry
const MOCK_REGISTRY = {
    'total-conducted': getMockTotalScansResponse,
    'turnaround-time': getMockTurnaroundTimeResponse,
    'patient-count': getMockPatientCountResponse,
    'monthly-trends': getMockMonthlyTrendsResponse,
    'location': getMockLocationResponse,
};

const getAuthHeaders = () => ({
    headers: { "Authorization": localStorage.getItem("jwt") }
});

const buildOutpatientsUrl = (endpoint, { uuidInvestigation, uuidDepartment, startDate, endDate }) => {
    const baseUrl = `${ANALYTICS_BASE_URL}/${uuidInvestigation}/radiology`;
    const endpointPath = endpoint || '';
    console.log('Building URL radiology:', endpointPath, uuidDepartment, startDate, endDate);
    return `${baseUrl}/${endpointPath}/${uuidDepartment}/startDate/${startDate}/endDate/${endDate}`;
};

const makeRequest = async (params) => {
    const { endpointPath } = params;
    const url = buildOutpatientsUrl(endpointPath, params);
    console.log('Making request to:', params, endpointPath);
    
    // Return mock data if enabled
    if (shouldServeMock(endpointPath, MOCK_CONFIG, MOCK_REGISTRY)) {
        console.log(`[MOCK] Returning mock data for: ${endpointPath}`);
        await new Promise(resolve => setTimeout(resolve, MOCK_CONFIG.delay));
        return MOCK_REGISTRY[endpointPath]();
    }
    
    try {
        const response = await axios.get(url, getAuthHeaders());
        if (response.status === 200) {
            console.log('radiology API Response:', endpointPath, response.data);
            return response.data;
        }
        console.log('radiology API Response Error:', endpointPath, response.data);
    } catch (error) {
        console.error('Error fetching data: radiology', url, error);
    }
};

const createEndpoint = (endpointPath) => {
    console.log('Creating radiology endpoint:', endpointPath);
    return datalogger((uuidInvestigation, uuidDepartment, startDate, endDate) => 
        makeRequest({ 
            endpointPath,
            uuidInvestigation, 
            uuidDepartment, 
            startDate, 
            endDate 
        })
    );
};

export const radiologyServices = {
    getTotalScans: createEndpoint('total-conducted'),
    getTurnaroundTime: createEndpoint('turnaround-time'), 
    getPatientCount: createEndpoint('patient-count'),
    getMonthlyTrends: createEndpoint('monthly-trends'),
    getLocation: createEndpoint('location')
};

export const RadiologyEndpoints = {
    STATS: '/total-scans',
    BY_DEPARTMENT: '/scans-by-department',
    BY_TYPE: '/scans-by-type',
    AVG_TURNAROUND_TIME: '/average-scan-time',
    MONTHLY_SCANS: '/monthly-scans',
    PENDING_SCANS: '/pending-scans',
    TEST_ACCURACY_RATE: '/test-accuracy-rate'
};
